import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Copy, Languages, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LANG_CATALOGUE = [
  ["en", "English"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"],
  ["pt", "Português"], ["it", "Italiano"], ["ja", "日本語"], ["ko", "한국어"],
  ["zh", "中文"], ["ar", "العربية"], ["hi", "हिन्दी"], ["ru", "Русский"],
];

function TranslationDialog({ open, onClose, entry, onSaved }) {
  const [lang, setLang] = useState("es");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [genre, setGenre] = useState("");
  const [existing, setExisting] = useState([]);
  useEffect(() => {
    if (entry) {
      setExisting(entry.translations || []);
      setLang("es"); setTitle(""); setDescription(""); setGenre("");
    }
  }, [entry]);
  if (!entry) return null;
  const editExisting = (t) => {
    setLang(t.lang);
    setTitle(t.title || "");
    setDescription(t.description || "");
    setGenre((t.genre || []).join(", "));
  };
  const removeExisting = async (t) => {
    // Send empty title/desc: still stored but explicitly cleared.
    // Simpler: PATCH with empty to overwrite; front-end filters on lang===lang
    try {
      await api.patch(`/epg/${entry.id}/translation`, {
        lang: t.lang, title: "", description: "", genre: [],
      });
      const filtered = existing.filter((x) => x.lang !== t.lang || x.title !== t.title);
      setExisting(filtered);
      onSaved({ ...entry, translations: filtered });
      toast.success(`Removed ${t.lang}`);
    } catch {
      toast.error("Remove failed");
    }
  };
  const save = async () => {
    try {
      const r = await api.patch(`/epg/${entry.id}/translation`, {
        lang, title, description,
        genre: genre.split(",").map((g) => g.trim()).filter(Boolean),
      });
      onSaved(r.data);
      setExisting(r.data.translations || []);
      toast.success(`${lang} translation saved`);
    } catch {
      toast.error("Save failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl" data-testid="epg-translation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Languages className="w-4 h-4" /> Translations · <span className="text-zinc-400">{entry.title}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 font-mono">
            Add per-language title + description strings; they will be embedded in every XMLTV feed as &lt;title lang=&quot;xx&quot;&gt; and &lt;desc lang=&quot;xx&quot;&gt; entries.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {existing.length > 0 && (
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Existing translations</Label>
              {existing.filter((t) => t.title || t.description).map((t, i) => (
                <div
                  key={`${t.lang}-${i}`}
                  data-testid={`existing-tr-${t.lang}`}
                  className="flex items-center gap-2 border border-zinc-800 rounded-sm px-2 py-1 bg-black/30"
                >
                  <span className="font-mono text-[10px] uppercase text-zinc-400 w-8">{t.lang}</span>
                  <span className="flex-1 truncate text-xs">{t.title || <em className="text-zinc-500">(no title)</em>}</span>
                  <button onClick={() => editExisting(t)} className="text-[10px] font-mono uppercase text-zinc-400 hover:text-zinc-100">Edit</button>
                  <button onClick={() => removeExisting(t)} className="text-red-400 hover:text-red-300" title="Clear translation">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-[100px_1fr] gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Language</Label>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                data-testid="translation-lang-select"
              >
                {LANG_CATALOGUE.map(([code, label]) => (
                  <option key={code} value={code}>{code} · {label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Translated title"
                className="mt-1 bg-black border-zinc-800 text-xs"
                data-testid="translation-title-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 bg-black border border-zinc-800 rounded-sm p-2 font-mono text-xs h-20"
              data-testid="translation-desc-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Genre (comma-separated)</Label>
            <Input
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              placeholder="Noticias, En vivo"
              className="mt-1 bg-black border-zinc-800 text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-zinc-800">Close</Button>
          <Button onClick={save} data-testid="translation-save-btn" className="rounded-sm tenant-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Save translation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EPG() {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [entries, setEntries] = useState([]);
  const [xml, setXml] = useState("");
  const [exportLang, setExportLang] = useState("all");
  const [editEntry, setEditEntry] = useState(null);

  useEffect(() => {
    api.get("/channels").then((r) => {
      setChannels(r.data);
      if (r.data[0]) setChannelId(r.data[0].id);
    });
  }, []);

  const reload = () => {
    if (!channelId) return;
    api.get("/epg", { params: { channel_id: channelId } }).then((r) => setEntries(r.data));
    const params = exportLang === "all"
      ? { all_langs: true }
      : exportLang === "default"
      ? {}
      : { lang: exportLang };
    api.get(`/epg/xmltv/${channelId}`, { params, transformResponse: (d) => d }).then((r) => setXml(r.data));
  };

  useEffect(() => { reload(); }, [channelId, exportLang]);

  const copyXml = () => { navigator.clipboard.writeText(xml); toast.success("XMLTV copied"); };
  const downloadXml = () => {
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `epg-${channelId}-${exportLang}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const trLangs = (e) => (e.translations || []).filter((t) => t.title || t.description).map((t) => t.lang);

  return (
    <div className="p-4">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ EPG</div>
          <h1 className="font-display font-bold text-2xl mt-1">Program guide · XMLTV · Multi-language</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={exportLang}
            onChange={(e) => setExportLang(e.target.value)}
            data-testid="epg-export-lang"
            className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-2 font-mono text-xs"
          >
            <option value="all">All languages</option>
            <option value="default">Default only</option>
            {LANG_CATALOGUE.map(([code, label]) => (
              <option key={code} value={code}>{code} · {label}</option>
            ))}
          </select>
          <Select value={channelId} onValueChange={setChannelId}>
            <SelectTrigger className="w-56 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="epg-channel-select">
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel">
          <div className="panel-header"><span className="panel-title">Upcoming programs · click row to translate</span></div>
          <div className="max-h-[70vh] overflow-auto scrollbar-thin">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-950">
                <tr className="text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-3 py-2">Start</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Languages</th>
                  <th className="text-right px-3 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const langs = trLangs(e);
                  return (
                    <tr
                      key={e.id}
                      className="border-b border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
                      onClick={() => setEditEntry(e)}
                      data-testid={`epg-row-${e.id}`}
                    >
                      <td className="px-3 py-1.5 font-mono text-zinc-300">
                        {new Date(e.start_time).toLocaleString([], { hour12: false })}
                      </td>
                      <td className="px-3 py-1.5">{e.title}</td>
                      <td className="px-3 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          <span className="font-mono text-[9px] uppercase border border-emerald-500/40 text-emerald-400 px-1 rounded-sm">en</span>
                          {langs.map((l) => (
                            <span key={l} className="font-mono text-[9px] uppercase border border-zinc-700 text-zinc-300 px-1 rounded-sm">{l}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-right text-zinc-500">
                        <Languages className="w-3.5 h-3.5 inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="panel flex flex-col">
          <div className="panel-header">
            <span className="panel-title">XMLTV feed · <span className="text-zinc-400 font-mono">{exportLang}</span></span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={copyXml} className="rounded-sm border-zinc-800 text-xs" data-testid="epg-copy-xml">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
              <Button size="sm" onClick={downloadXml} className="rounded-sm tenant-btn text-xs" data-testid="epg-download-xml">
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          </div>
          <pre className="p-3 overflow-auto text-[10px] font-mono text-emerald-300 max-h-[70vh] scrollbar-thin whitespace-pre-wrap">
            {xml}
          </pre>
        </div>
      </div>

      <TranslationDialog
        open={!!editEntry}
        entry={editEntry}
        onClose={() => { setEditEntry(null); reload(); }}
        onSaved={(updated) => {
          setEntries((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          setEditEntry(updated);
        }}
      />
    </div>
  );
}
